import React, { useState, useEffect } from 'react';

import { dataGridPremiumColumns } from '../../../utils/helper';
import { DataGridIdentifiers, DatagridPremiumUi } from '../../Interface';
import productionService from '../../../service/production.service';
import dayjs from 'dayjs';
import { isEqual } from 'lodash';
import useGenerateJokerReportCols from '../../../hooks/useGenerateJokerReportCols';

export default function ProductionReports({ productionState, selectedOrderData }) {
  const {
    setSelectedValues,
    viewField,
    operatorsDisabled,
    setRefetchTable,
    rowSelectionModel,
    setRowSelectionModel,
    refetchTable,
    autocompleteOptions,
    jokerFields,
  } = productionState;

  const { selectedOrder, selectedLineNum } = selectedOrderData || {};

  const [reports, setReports] = useState({ rows: [], rowCount: 0 });
  const [filteredOrderNumbers, setFilteredOrderNumbers] = useState([]);

  const [initialState, setInitialState] = useState({
    filter: { filterModel: { items: [], logicOperator: 'and' } },
    sorting: {
      sortModel: [{ field: 'workingNumber', sort: 'asc' }],
    },
    pagination: {
      paginationModel: {
        pageSize: 25,
        page: 0,
      },
    },
    density: 'compact',
  });

  useEffect(() => {
    if (operatorsDisabled) {
      setRowSelectionModel([]);
    }
  }, [operatorsDisabled]);

  useEffect(() => {
    if (selectedOrder) {
      getProds();
    }
  }, [selectedOrder]);

  const getProds = async () => {
    const data = await productionService.getProductionByOrderNumber({ selectedOrder, selectedLineNum });

    const fProd = data.map((item) => item.id);
    setFilteredOrderNumbers(fProd);

    const initState = {
      filter: {
        filterModel: {
          items: fProd.map((ordnum) => ({
            field: 'id',
            operator: 'equals',
            value: ordnum,
          })),

          logicOperator: 'or',
        },
      },
    };
    if (isEqual(initState, initialState)) return initialState;
    setInitialState(initState);
    return initState;
  };

  const getList = async (gridState) => {
    if (selectedOrder) {
      const query = await getProds();
      if (query.filter.filterModel.items.length === 0) {
        setReports({ rows: [], rowCount: 0 });
        return;
      }
      productionService.getProductions(query).then((data) => {
        setReports(data);
      });
    } else {
      productionService.getProductions(gridState).then((data) => {
        setReports(data);
      });
    }
  };

  const jokerCols = useGenerateJokerReportCols(jokerFields);

  const columns = dataGridPremiumColumns([
    {
      field: 'workingNumber',
      headerName: 'Munkaszám',
      type: 'string',
    },
    {
      field: 'baseDocNum',
      headerName: 'Vevői rendelés szám',
      type: 'string',
    },
    {
      field: 'articleNumber',
      headerName: 'Cikkszám',
      type: 'string',
    },
    {
      field: 'articleName',
      headerName: 'Cikknév',
      type: 'string',
    },
    {
      field: 'finishedItemCode',
      headerName: 'Késztermék cikkszám',
      type: 'string',
    },
    {
      field: 'finishedItemName',
      headerName: 'Késztermék cikknév',
      type: 'string',
    },
    {
      field: 'quantity',
      headerName: 'Cikkszám mennyiség',
      type: 'number',
    },
    {
      field: 'finishedItemQuantity',
      headerName: 'Késztermék mennyiség',
      type: 'number',
    },
    {
      field: 'productionQuantity',
      headerName: 'Mennyiség',
      type: 'number',
    },
    {
      field: 'me',
      headerName: 'Me.',
      type: 'singleSelect',
      sortable: false,
      renderCell: (params) => {
        return params.row.quantityUnit?.code;
      },
      valueOptions: autocompleteOptions.quantityUnit,
    },
    {
      field: 'startDate',
      type: 'date',
      headerName: 'Kezdő időpont',
      valueGetter: (_data, row) => {
        return new Date(row.startDate);
      },
      renderCell: (data) => {
        return dayjs(data.row.startDate).format('YYYY.MM.DD HH:mm');
      },
    },
    {
      field: 'endDate',
      type: 'date',
      headerName: 'Befejező időpont',
      valueGetter: (_data, row) => {
        return new Date(row.endDate);
      },
      renderCell: (data) => {
        return dayjs(data.row.endDate).format('YYYY.MM.DD HH:mm');
      },
    },
    {
      field: 'projectId',
      headerName: 'Projekt',
      type: 'singleSelect',
      sortable: false,
      renderCell: (data) => {
        return data.row.project?.projectName;
      },
      valueOptions: autocompleteOptions.project,
    },
    {
      field: 'productionStatusId',
      headerName: 'Gyártás státusz',
      type: 'singleSelect',
      sortable: false,
      renderCell: (params) => {
        return params.row.productionStatus?.name;
      },
      valueOptions: autocompleteOptions.prodStatus,
    },
    {
      field: 'priorityId',
      headerName: 'Prioritás',
      type: 'singleSelect',
      sortable: false,
      renderCell: (params) => {
        return params.row.priority?.itemName;
      },
      valueOptions: autocompleteOptions.priority,
    },
    {
      field: 'companyId',
      headerName: 'Vevő neve',
      type: 'singleSelect',
      sortable: false,
      renderCell: (params) => {
        return params.row.company?.name;
      },
      valueOptions: autocompleteOptions.company,
    },
    {
      field: 'baseId',
      headerName: 'VERE id',
      type: 'number',
    },
    {
      field: 'baseLine',
      headerName: 'VERE sor',
      type: 'number',
    },
    {
      field: 'reference',
      headerName: 'Vevőhiv.',
      type: 'string',
    },
    {
      field: 'userId',
      headerName: 'Tulajdonos',
      type: 'singleSelect',
      sortable: false,
      renderCell: (params) => {
        return params.row.user?.userName;
      },
      valueOptions: autocompleteOptions.user,
    },
    ...jokerCols,
  ]);

  return (
    <DatagridPremiumUi
      datagridIdentifier={DataGridIdentifiers.ProductionReports}
      datagridInitialState={initialState}
      filterFromInitialState={filteredOrderNumbers.length > 0}
      dataLoader={getList}
      rows={reports.rows}
      refetch={{ refetchTable, setRefetchTable }}
      rowCount={reports.rowCount}
      columns={[...columns]}
      onRowClick={(rowData) => {
        if (!viewField) return;
        setSelectedValues(rowData.row);
      }}
      paginationAndfilterMode={filteredOrderNumbers.length > 0 ? 'client' : 'server'}
      onRowSelectionModelChange={(newRowSelectionModel) => {
        setRowSelectionModel(newRowSelectionModel);
      }}
      rowSelectionModel={rowSelectionModel}
      disableRowSelectionOnClick={!viewField}
    />
  );
}
